import { useCallback, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { trpc } from '../lib/trpc';
import { PageContainer } from '../components/PageContainer';
import { Button } from '../components/ui/Button';
import { WineForm } from '../components/WineForm';
import {
  StagedImageUploader,
  type StagedFile,
} from '../components/MediaLibrary/StagedImageUploader';
import { useImageUpload } from '../hooks/useImageUpload';
import type { WineFormData } from '@wineryxplorer/shared';

type CreateIntent = 'navigate' | 'addAnother';

interface LastCreatedSummary {
  id: string;
  name: string;
  imageFailures: string[];
  updateFailed: boolean;
}

export const WineCreate = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const venueId = searchParams.get('venueId') || undefined;

  const [error, setError] = useState<string | null>(null);
  const [lastCreated, setLastCreated] = useState<LastCreatedSummary | null>(null);
  const [isBusy, setIsBusy] = useState(false);
  const [busyLabel, setBusyLabel] = useState<string | null>(null);
  const [intent, setIntent] = useState<CreateIntent | null>(null);
  const [heroStaged, setHeroStaged] = useState<StagedFile[]>([]);
  const [galleryStaged, setGalleryStaged] = useState<StagedFile[]>([]);
  const [formResetKey, setFormResetKey] = useState(0);

  const formSubmitRef = useRef<(() => Promise<void>) | null>(null);
  const pendingFormDataRef = useRef<WineFormData | null>(null);

  const createWine = trpc.wine.create.useMutation();
  const updateWine = trpc.wine.update.useMutation();
  const { uploadImage } = useImageUpload();

  const resetStagedImages = useCallback(() => {
    [...heroStaged, ...galleryStaged].forEach((f) => URL.revokeObjectURL(f.previewUrl));
    setHeroStaged([]);
    setGalleryStaged([]);
  }, [heroStaged, galleryStaged]);

  const markStagedStatus = (
    setter: React.Dispatch<React.SetStateAction<StagedFile[]>>,
    id: string,
    status: StagedFile['status'],
    errorMessage?: string
  ) => {
    setter((prev) => prev.map((f) => (f.id === id ? { ...f, status, errorMessage } : f)));
  };

  const uploadStaged = async (
    wineId: string,
    staged: StagedFile,
    setter: React.Dispatch<React.SetStateAction<StagedFile[]>>
  ): Promise<{ mediaId: string | null; failure: string | null }> => {
    markStagedStatus(setter, staged.id, 'uploading');
    try {
      const result = await uploadImage(staged.file, {
        entityType: 'wine',
        entityId: wineId,
      });
      markStagedStatus(setter, staged.id, 'completed');
      return { mediaId: result.mediaId, failure: null };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Upload failed';
      markStagedStatus(setter, staged.id, 'error', message);
      return { mediaId: null, failure: `${staged.file.name}: ${message}` };
    }
  };

  const runUploadChain = async (
    wineId: string,
    wineName: string
  ): Promise<LastCreatedSummary> => {
    const imageFailures: string[] = [];
    let heroMediaId: string | undefined;
    const galleryMediaIds: string[] = [];

    if (heroStaged.length > 0) {
      setBusyLabel('Uploading hero image...');
      const hero = heroStaged[0];
      const { mediaId, failure } = await uploadStaged(wineId, hero, setHeroStaged);
      if (mediaId) heroMediaId = mediaId;
      if (failure) imageFailures.push(failure);
    }

    for (let i = 0; i < galleryStaged.length; i++) {
      setBusyLabel(`Uploading supporting image ${i + 1} of ${galleryStaged.length}...`);
      const { mediaId, failure } = await uploadStaged(wineId, galleryStaged[i], setGalleryStaged);
      if (mediaId) galleryMediaIds.push(mediaId);
      if (failure) imageFailures.push(failure);
    }

    let updateFailed = false;
    if (heroMediaId || galleryMediaIds.length > 0) {
      setBusyLabel('Finalizing...');
      const payload = {
        id: wineId,
        heroImageId: heroMediaId,
        imageIds: galleryMediaIds,
      };
      try {
        await updateWine.mutateAsync(payload);
      } catch {
        try {
          await updateWine.mutateAsync(payload);
        } catch {
          updateFailed = true;
        }
      }
    }

    return { id: wineId, name: wineName, imageFailures, updateFailed };
  };

  const handleFormSubmit = async (data: WineFormData) => {
    // WineForm calls this with validated data — stash it and continue the chain
    // outside WineForm so we control the whole flow.
    pendingFormDataRef.current = data;
  };

  const performCreate = async (targetIntent: CreateIntent) => {
    if (isBusy) return;
    setError(null);
    setLastCreated(null);
    setIntent(targetIntent);
    setIsBusy(true);
    setBusyLabel('Creating wine...');

    try {
      pendingFormDataRef.current = null;
      if (!formSubmitRef.current) {
        throw new Error('Form not ready');
      }
      await formSubmitRef.current();
      // WineForm skips its onSubmit callback when validation fails, so the ref
      // stays null. ESLint can't see that, hence the disable.
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      const formData = pendingFormDataRef.current;
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (!formData) {
        setIsBusy(false);
        setBusyLabel(null);
        setIntent(null);
        return;
      }

      const created = await createWine.mutateAsync(formData);

      const summary = await runUploadChain(created.id, created.name);
      setLastCreated(summary);

      if (summary.imageFailures.length > 0 || summary.updateFailed) {
        // Stay on page so user sees the error banner.
        setIsBusy(false);
        setBusyLabel(null);
        setIntent(null);
        return;
      }

      if (targetIntent === 'navigate') {
        // Started from the venue management hub? Go back to it — the admin is
        // still filling out that venue. 'Create and add another' keeps its own
        // stay-here behaviour either way.
        navigate(venueId ? `/wineries/${venueId}/manage` : `/wines/${created.id}`);
        return;
      }

      // addAnother: reset the form + staged images, stay here, show success banner.
      resetStagedImages();
      setFormResetKey((k) => k + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create wine';
      setError(message);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setIsBusy(false);
      setBusyLabel(null);
      setIntent(null);
    }
  };

  const handleCreateClick = () => {
    void performCreate('navigate');
  };

  const handleCreateAndAddAnotherClick = () => {
    void performCreate('addAnother');
  };

  const handleCancel = () => {
    // Back to wherever the flow started: the venue's management hub, or the
    // public wine list for a wine created outside a venue.
    if (venueId) {
      navigate(`/wineries/${venueId}/manage`);
    } else {
      navigate('/wines');
    }
  };

  const primaryButtonLabel =
    isBusy && intent === 'navigate' ? (busyLabel ?? 'Creating...') : 'Create';
  const addAnotherButtonLabel =
    isBusy && intent === 'addAnother' ? (busyLabel ?? 'Creating...') : 'Create & Add Another';

  const totalImageCount = heroStaged.length + galleryStaged.length;

  return (
    <div className="px-0 md:px-4 pt-2 pb-4 max-w-4xl mx-auto">
      <PageContainer className="mt-2">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-6">Create Wine</h1>

          <div className="flex flex-wrap gap-3 mb-6 justify-end">
            <Button
              variant="primary"
              onClick={handleCreateClick}
              disabled={isBusy}
              loading={isBusy && intent === 'navigate'}
            >
              {primaryButtonLabel}
            </Button>
            <Button
              variant="outline"
              onClick={handleCreateAndAddAnotherClick}
              disabled={isBusy}
              loading={isBusy && intent === 'addAnother'}
            >
              {addAnotherButtonLabel}
            </Button>
            <Button variant="outline" onClick={handleCancel} disabled={isBusy}>
              Cancel
            </Button>
          </div>
        </div>

        {lastCreated &&
          (lastCreated.imageFailures.length > 0 || lastCreated.updateFailed) && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
              <h3 className="text-sm font-medium text-red-800">
                Wine created with issues
              </h3>
              {lastCreated.imageFailures.length > 0 && (
                <div className="mt-2">
                  <p className="text-sm text-red-700">
                    {lastCreated.imageFailures.length} image
                    {lastCreated.imageFailures.length === 1 ? '' : 's'} failed to upload:
                  </p>
                  <ul className="mt-1 text-xs text-red-700 list-disc list-inside space-y-0.5">
                    {lastCreated.imageFailures.map((f, i) => (
                      <li key={i}>{f}</li>
                    ))}
                  </ul>
                </div>
              )}
              {lastCreated.updateFailed && (
                <p className="mt-2 text-sm text-red-700">
                  Images uploaded but not attached to wine. Open the wine editor to select them.
                </p>
              )}
              <div className="mt-3">
                <Link
                  to={`/wines/${lastCreated.id}/edit?tab=media`}
                  className="text-sm font-medium text-red-800 underline hover:text-red-900"
                >
                  Go to Wine
                </Link>
              </div>
            </div>
          )}

        {lastCreated?.imageFailures.length === 0 && !lastCreated.updateFailed && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-md flex items-center justify-between">
              <p className="text-sm text-green-800">
                &ldquo;{lastCreated.name}&rdquo; created successfully.
              </p>
              <Link
                to={`/wines/${lastCreated.id}`}
                className="text-sm font-medium text-green-800 underline hover:text-green-900"
              >
                View wine
              </Link>
            </div>
          )}

        {error && (
          <div className="mt-2 mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
            <div className="flex">
              <svg
                className="w-5 h-5 text-red-400 mr-3 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div>
                <h3 className="text-sm font-medium text-red-800">Error creating wine</h3>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
            </div>
          </div>
        )}

        <div className="mt-6">
          <WineForm
            key={formResetKey}
            onSubmit={handleFormSubmit}
            onCancel={handleCancel}
            venueId={venueId}
            isSubmitting={isBusy}
            hideButtons
            submitRef={formSubmitRef}
          />
        </div>

        <div className="mt-6 border-t border-gray-200 pt-6">
          <StagedImageUploader
            mode="single"
            value={heroStaged}
            onChange={setHeroStaged}
            label="Hero Image"
            helperText="A tall, portrait-orientation bottle shot — roughly 9:16 aspect ratio (like a phone screenshot). Square or landscape photos will letterbox on the wine page."
            disabled={isBusy}
          />

          <StagedImageUploader
            mode="multi"
            value={galleryStaged}
            onChange={setGalleryStaged}
            label="Supporting Images"
            helperText="Additional photos — label close-ups, vineyard shots, tasting notes, etc."
            maxFiles={10}
            disabled={isBusy}
          />

          {totalImageCount > 0 && !isBusy && (
            <p className="mt-4 text-xs text-gray-500">
              {totalImageCount} image{totalImageCount === 1 ? '' : 's'} will be uploaded after
              the wine is created.
            </p>
          )}
        </div>
      </PageContainer>
    </div>
  );
};
